import {
  Injectable,
  computed,
  inject,
  signal,
  DestroyRef,
  effect,
  LOCALE_ID
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { setUser } from '@sentry/angular';
import {
  Observable,
  of,
  switchMap,
  tap
} from 'rxjs';

import { BrowserApiService } from '@core/common';
import { environment, SupportedLocale } from '@env';
import { listenWebsocketEvents } from '@core/websocket';
import { hasOverlap, syncWithLocalStorage } from '@core/helpers';
import { WINDOW } from '@core/common/tokens';
import { IntegrationsState } from '@app/modules/users/integrations.state';

import { NotificationsState } from '../notifications/notifications.state';

import {
  BetType,
  Counters,
  FooterCounters,
  LoggedUser,
  RestrictionType,
  STAFF_ROLES,
  TELEGRAM_HEADERS,
  TelegramBotData,
  Wager
} from './users.entity';
import { AuthService } from './auth.service';

declare global {
  interface Window {
    Intercom?: (method: string, data?: any) => void;
  }
}

// avoid dependecy injection cycle
export const TOKEN = signal<string | null>(null);
const USER = signal<LoggedUser | null>(null);

export function logout(): void {
  TOKEN.set(null);
  USER.set(null);
  setUser(null);
}

@Injectable({
  providedIn: 'root'
})
export class AuthState {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationsState = inject(NotificationsState);
  private readonly integrationsState = inject(IntegrationsState);
  private readonly window = inject(WINDOW);
  private readonly browser = inject(BrowserApiService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  public readonly token = TOKEN;
  public readonly user = USER;
  public readonly countryCode = signal<string | null>(null);
  public readonly referralBlocked = signal<boolean>(true);
  public readonly timeDiff = signal<number>(0);
  public readonly betType = signal<BetType>(BetType.BALANCE);
  public readonly betAmountUsd = signal<number>(0.25);
  public readonly isNewVersion = signal<boolean>(false);
  public readonly marketingCode = signal<null | string>(null);
  public readonly xplayEventId = signal<null | string>(null);
  public readonly lastUrl = signal<string>('/');
  public readonly counters = signal<Counters>({
    battleship: { bank: 0, count: 0 },
    crash: { bank: 0, count: 0 },
    wheel: { bank: 0, count: 0 },
    super_wheel: { bank: 0, count: 0 },
    double: { bank: 0, count: 0 },
    jackpot: { bank: 0, count: 0 },
    defuse: { bank: 0, count: 0 },
    mines: { bank: 0, count: 0 },
    'lucky-drop': { bank: 0, count: 0 }
  });

  public readonly currentLocale = inject<SupportedLocale>(LOCALE_ID);

  public readonly ukLocale = this.currentLocale === 'uk';

  public telegramData = signal<TelegramBotData | null>(
    localStorage.getItem(TELEGRAM_HEADERS) ? JSON.parse(localStorage.getItem(TELEGRAM_HEADERS) as string) : null
  );

  public readonly footerCounters = signal<FooterCounters>({
    usersCount: 0,
    gamesCount: 0,
    usersWonAmount: '0'
  });

  public readonly userRoles = computed(() => {
    const user = this.user();

    if (!user) {
      return ['IS_ANONYMOUS'];
    }

    return user.roles;
  });

  public readonly userIsStaff = computed(() => hasOverlap(STAFF_ROLES, this.userRoles()));
  public readonly isAuthenticated = computed(() => !!this.user());

  private readonly wsEvents = listenWebsocketEvents({
    'users.update_balance': (data) => this.onWsUpdateBalance(data),
    'users.update_inventory_price': (data) => this.onWsUpdateInventoryPrice(data),
    'users.new_level': (data) => this.onWsNewLevel(data),
    'users.level_exp_updated': (data) => this.onWsLevelExpUpdated(data),
    'users.wager_updated': (data) => this.onWsWagerUpdate(data),
    'users.wager_cancelled': () => this.onWsWagerDone(),
    'users.wager_done': () => this.onWsWagerDone(),
    'counters.update': (data) => this.onWsUpdateCounters(data),
    'footer_counters.update': (data) => this.onWsUpdateFooterCounters(data)
  });

  private readonly localStorageEffect = syncWithLocalStorage('auth', {
    betAmountUsd: this.betAmountUsd,
    betType: this.betType,
    timeDiff: this.timeDiff,
    countryCode: this.countryCode,
    user: this.user,
    token: this.token,
    new: this.isNewVersion,
    marketingCode: this.marketingCode,
    lastUrl: this.lastUrl,
    xplayEventId: this.xplayEventId
  });

  private readonly uidEffect = effect(() => {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const userId = this.user()?.id;
    const userCreatedAt = this.user()?.createdAt
      ? new Date(this.user()!.createdAt).toISOString().split('T')[0].replace(/-/g, '')
      : '';

    localStorage.setItem('ga_uid', userId?.toString() || '');
    localStorage.setItem('userCreatedAt', userCreatedAt);
  });

  constructor() {
    if (!this.isNewVersion()) {
      this.isNewVersion.set(true);
      this.betType.set(BetType.BALANCE);
    }

    if (this.browser.isBrowser()) {
      this.checkStatus().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    this.getXplayEventId().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  checkStatus() {
    return this.authService.getStatus().pipe(
      tap((response) => {
        if (null !== response.user) {
          response.user.balanceUsd = Number(response.user.balanceUsd);
          response.user.inventoryPriceUsd = Number(response.user.inventoryPriceUsd);
          response.user.wagerBalanceUsd = Number(response.user.wagerBalanceUsd);
        }

        this.user.set(response.user);
        this.countryCode.set(response.countryCode);
        this.counters.set(response.counters);
        this.footerCounters.set(response.footerCounters);
        this.referralBlocked.set(!!response.referralBlocked);

        this.setTimeDiff(response.serverTime);
        this.setSentryUser(response.user);
        this.bootIntercom(response.user, response.intercomToken);
      }),
      switchMap((response) => {
        const currentUrl = this.lastUrl();
        const eventId = this.xplayEventId();

        if (eventId) {
          if (response.user) {
            this.router.navigate([currentUrl]);

            this.integrationsState
              .sendType(eventId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                this.xplayEventId.set(null);
              });
          } else if (response.user === null) {
            this.router.navigate([currentUrl]);
          }
        }

        if (response.notifications.unread > 0) {
          return this.notificationsState.loadUnread();
        }

        return of(response);
      })
    );
  }

  setToken(token: string): Observable<any> {
    if (this.token() === token) {
      return of(true);
    }

    this.token.set(token);

    return this.checkStatus();
  }

  setBetType(betType: BetType): void {
    this.betType.set(betType);
  }

  setBetAmount(amount: number): void {
    this.betAmountUsd.set(amount);
  }

  patchUser<K extends keyof LoggedUser>(key: K, value: LoggedUser[K]): void {
    this.user.update((user) => {
      if (!user) {
        return null;
      }

      return {
        ...user,
        [key]: value
      };
    });
  }

  setTimeDiff(serverTimestamp: string): void {
    const timeDiff = this.calcTimeDiff(serverTimestamp);

    this.timeDiff.set(timeDiff);
  }

  saveLastUrl(url?: string): void {
    this.lastUrl.set(url || this.getCurrentUrlForSave());
  }

  getCurrentUrlForSave(): string {
    const urlTree = this.router.parseUrl(this.router.url);

    urlTree.queryParams = {};
    urlTree.fragment = null;

    return urlTree.toString();
  }

  acceptTerms(temp = false): void {
    if (temp) {
      this.patchUser('isTosRead', true);
    } else {
      this.authService
        .acceptTerms()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.patchUser('isTosRead', true));
    }
  }

  claimTelegramBonus(): Observable<void> {
    return this.authService.claimTelegramBonus().pipe(
      tap(() => {
        const user = this.user();

        if (!user || !user.telegramAccount) {
          return;
        }

        this.patchUser('telegramAccount', {
          ...user.telegramAccount,
          hasClaimedBonus: true,
          subscribedToChannel: true
        });
      })
    );
  }

  claimVkBonus(): Observable<void> {
    return this.authService.claimVkBonus().pipe(
      tap(() => {
        const user = this.user();

        if (!user || !user.vkAccount) {
          return;
        }

        this.patchUser('vkAccount', {
          ...user.vkAccount,
          hasClaimedBonus: true,
          subscribedToChannel: true
        });
      })
    );
  }

  cancelWager(): Observable<void> {
    return this.authService.cancelWager();
  }

  createRestriction(
    userId: number,
    autoremoveAt: number | null,
    type: RestrictionType,
    context: any
  ): Observable<void> {
    return this.authService.createRestriction(userId, autoremoveAt, type, context);
  }

  setMarketingCode(code: string | null): void {
    this.marketingCode.set(code);
  }

  logout(): void {
    logout();
  }

  private getXplayEventId() {
    return this.activatedRoute.queryParams.pipe(
      tap((params) => {
        const utmSource = params['utm_source'];
        const utmMedium = params['utm_medium'];
        const utmCampaign = params['utm_campaign'];

        if (utmCampaign && utmSource === 'xplaygg' && utmMedium === 'missions') {
          this.xplayEventId.set(utmCampaign);
        }
      })
    );
  }

  private setSentryUser(user: LoggedUser | null): void {
    if (user) {
      const id = String(user.id);

      setUser({ id, username: user.nickname });
    } else {
      setUser(null);
    }
  }

  private calcTimeDiff(serverTimestamp: string): number {
    const serverTime = Number(new Date(serverTimestamp));

    return Date.now() - serverTime;
  }

  private onWsUpdateBalance(balance: number | string): void {
    this.patchUser('balanceUsd', Number(balance));
  }

  private onWsUpdateInventoryPrice(invPrice: number): void {
    this.patchUser('inventoryPriceUsd', Number(invPrice));
  }

  private onWsNewLevel(level: number): void {
    this.patchUser('level', level);
  }

  private onWsLevelExpUpdated(levelExp: number): void {
    this.patchUser('levelExp', levelExp);
  }

  private onWsWagerUpdate({ wager, wagerBalanceUsd }: { wager: Wager; wagerBalanceUsd: string }): void {
    this.patchUser('wager', wager);
    this.patchUser('wagerBalanceUsd', Number(wagerBalanceUsd));
  }

  private onWsWagerDone(): void {
    this.patchUser('wager', null);
    this.patchUser('wagerBalanceUsd', 0);
  }

  private onWsUpdateCounters(counters: Counters): void {
    this.counters.set({
      ...this.counters(),
      ...counters
    });
  }

  private onWsUpdateFooterCounters(counters: FooterCounters): void {
    this.footerCounters.set({ ...counters });
  }

  private bootIntercom(user: LoggedUser | null, token: string | null): void {
    const isSupportedIntercom = localStorage.getItem('isSupportedIntercom') !== 'false';

    if (!isSupportedIntercom || !this.window || !this.window.Intercom) {
      return;
    }

    let userData: Record<string, any> = {};

    if (user) {
      userData = {
        user_id: user.id,
        user_hash: token,
        name: user.nickname,
        avatar: {
          type: 'avatar',
          image_url: user.photoUrl
        }
      };
    }

    this.window.Intercom('boot', {
      ...userData,
      app_id: environment.intercomAppId,
      custom_launcher_selector: '.intercomTrigger',
      hide_default_launcher: true,
      ...(this.ukLocale && { language_override: 'uk' })
    });
  }
}
